import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import {
  CalendarRange, PoundSterling, Users, Sparkles, MessageCircle,
  Wifi, Trash2, KeyRound, ChefHat, Briefcase, ShieldCheck, WashingMachine, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import { submitContact, fetchProperty } from "../lib/api";
import { usePageMeta, pushDataLayer } from "../lib/pageMeta";
import PageHeader from "../components/PageHeader";

const PROPERTY_ID = "ed57dee3-541b-4c9a-b13a-6c8c926acea5";
const WHATSAPP_URL = "https://wa.me/447474759947";

// Long-stay guests want to picture living/working there, not sleeping there.
// Prioritise the existing kitchen and living images first, bedroom last.
// Keys are unique fragments of each image's Airbnb CDN filename — order-only,
// no crops, no replacements, no new assets.
const PHOTO_PRIORITY = [
  "6080d2a0", // kitchen (oven, hob, breakfast bar)
  "dab13285", // kitchen (sink, wooden countertop)
  "58192b3a", // living space (TV + cabinet)
  "535da426", // bedroom
  "6db9c278", // bedroom + kitchenette peek
  "150c8aa0", // bedroom
];

function reorderPhotos(images) {
  return [...images].sort((a, b) => {
    const ia = PHOTO_PRIORITY.findIndex((k) => a.includes(k));
    const ib = PHOTO_PRIORITY.findIndex((k) => b.includes(k));
    const sa = ia === -1 ? PHOTO_PRIORITY.length : ia;
    const sb = ib === -1 ? PHOTO_PRIORITY.length : ib;
    return sa - sb;
  });
}

const STAY_ITEMS = [
  { icon: CalendarRange, title: "90 nights",                 body: "3 January to 3 April 2027" },
  { icon: PoundSterling, title: "£2,950 per calendar month", body: "All bills included" },
  { icon: Users,         title: "Two occupants",             body: "Furnished studio" },
  { icon: Sparkles,      title: "Bills, Wi-Fi & housekeeping", body: "Weekly clean and fresh linen" },
];

const INCLUDED = [
  { icon: ShieldCheck,      text: "All utilities and council tax" },
  { icon: Wifi,             text: "300 Mb fibre, video-call ready" },
  { icon: Trash2,           text: "Weekly housekeeping and fresh linen" },
  { icon: ChefHat,          text: "Fully equipped kitchen" },
  { icon: Briefcase,        text: "Bar-height kitchen island with stools — room to work" },
  { icon: WashingMachine,   text: "Washing machine in the apartment" },
  { icon: KeyRound,         text: "Self check-in at any hour" },
  { icon: Sparkles,         text: "No agency fees and no platform charges" },
];

const LOCATION_FACTS = [
  // Reused from the existing neighbourhood copy in the codebase.
  { label: "Hammersmith Station",  note: "Three lines · 22 min to Mayfair" },
  { label: "River Café",           note: "Eight minutes by foot" },
  { label: "Thames Path",          note: "Hammersmith Bridge to Barnes — a two-mile riverside walk" },
  { label: "King Street",          note: "Independent coffee, restaurants and Hammersmith Apollo on the doorstep" },
  { label: "Charing Cross Hospital",              note: "A short walk from the apartment" },
  { label: "Imperial College, Charing Cross Campus", note: "A short walk from the apartment" },
];

export default function LongStay() {
  usePageMeta({
    title: "Long Stay Serviced Apartment Hammersmith | £2,950/Month | West London Stays",
    description:
      "Furnished serviced studio in Hammersmith W6, available as a 90-night let from 3 January 2027. £2,950 per calendar month, all bills, Wi-Fi and weekly housekeeping included. Book direct, no agency fees.",
  });

  const [images, setImages] = useState([]);
  useEffect(() => {
    fetchProperty(PROPERTY_ID).then((p) => setImages(reorderPhotos(p?.images || []))).catch(() => {});
  }, []);

  const formRef = useRef(null);
  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div data-testid="long-stay-page" className="bg-[#050505] text-white -mb-24">
      <PageHeader
        overline="Long Stay"
        title="Three months in Hammersmith, from 3 January."
        subtitle="A self-contained furnished studio available as a single 90-night stay. £2,950 per calendar month, all bills included."
        image={images[0] || "https://images.unsplash.com/photo-1656789280583-e06c4f88f8eb?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"}
      />

      {/* Hero CTAs */}
      <section className="pt-10 pb-4">
        <div className="container mx-auto px-6 md:px-12">
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              onClick={scrollToForm}
              data-testid="hero-request-availability"
              className="btn-gold justify-center"
            >
              Request availability <ArrowRight size={16} />
            </button>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="hero-whatsapp"
              className="btn-emerald justify-center"
            >
              <MessageCircle size={14} /> Message the host
            </a>
          </div>
        </div>
      </section>

      {/* The stay */}
      <section className="py-16">
        <div className="container mx-auto px-6 md:px-12">
          <p className="overline mb-4">The stay</p>
          <h2 className="font-serif text-3xl sm:text-4xl text-white mb-10 max-w-2xl">
            What's on offer.
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="stay-items">
            {STAY_ITEMS.map((s, i) => (
              <div key={i} data-testid={`stay-item-${i}`} className="card-flat p-7">
                <div className="w-11 h-11 border border-gold/40 flex items-center justify-center text-gold mb-5">
                  <s.icon size={18} />
                </div>
                <h3 className="font-serif text-xl text-white mb-2">{s.title}</h3>
                <p className="text-white/60 text-sm leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What's included */}
      <section className="py-16 bg-[#0a0d0b] border-y border-white/5">
        <div className="container mx-auto px-6 md:px-12">
          <p className="overline mb-4">What's included</p>
          <h2 className="font-serif text-3xl sm:text-4xl text-white mb-10 max-w-2xl">
            Everything covered. One monthly figure.
          </h2>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-4" data-testid="included-list">
            {INCLUDED.map((it, i) => (
              <li key={i} data-testid={`included-${i}`} className="flex items-start gap-3 border-b border-white/5 pb-3">
                <it.icon size={16} className="text-gold mt-1 flex-shrink-0" />
                <span className="text-white/75 text-sm">{it.text}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* The location */}
      <section className="py-16">
        <div className="container mx-auto px-6 md:px-12">
          <p className="overline mb-4">The location</p>
          <h2 className="font-serif text-3xl sm:text-4xl text-white mb-6 max-w-2xl">
            Hammersmith, W6.
          </h2>
          <p className="text-white/65 max-w-2xl mb-10 leading-relaxed">
            W6 sits between the river and the West End. Old pubs, indie roasters, riverside walks, and tube connections that get you to any meeting on time.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-testid="location-facts">
            {LOCATION_FACTS.map((f, i) => (
              <div key={i} data-testid={`location-fact-${i}`} className="card-flat p-6">
                <p className="font-serif text-lg text-white mb-1">{f.label}</p>
                <p className="text-white/55 text-sm">{f.note}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who it suits */}
      <section className="py-14 bg-[#0a0d0b] border-y border-white/5">
        <div className="container mx-auto px-6 md:px-12 max-w-3xl">
          <p className="overline mb-4">Who it suits</p>
          <p className="font-serif text-2xl sm:text-3xl text-white leading-relaxed" data-testid="who-it-suits">
            Relocating professionals, visiting clinicians and academics, contractors on assignment, and families between homes.
          </p>
        </div>
      </section>

      {/* Photos — reuse existing apartment images */}
      {images.length > 0 && (
        <section className="py-16">
          <div className="container mx-auto px-6 md:px-12">
            <p className="overline mb-4">Photos</p>
            <h2 className="font-serif text-3xl sm:text-4xl text-white mb-10 max-w-2xl">
              Inside the apartment.
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4" data-testid="photo-grid">
              {images.slice(0, 6).map((src, i) => (
                <div key={i} data-testid={`photo-${i}`} className="aspect-[4/3] overflow-hidden">
                  <img src={src} alt={`Apartment view ${i + 1}`} loading="lazy" className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Company and agency lets — reuses "Who it suits" section styling (dark stripe, container, overline, serif heading, existing button). */}
      <section className="py-14 bg-[#0a0d0b] border-y border-white/5">
        <div className="container mx-auto px-6 md:px-12 max-w-3xl">
          <p className="overline mb-4">Company and agency lets</p>
          <h2 className="font-serif text-3xl sm:text-4xl text-white mb-6 leading-tight">Company and agency lets</h2>
          <p className="text-white/75 leading-relaxed mb-8" data-testid="company-lets-body">
            We let to companies as well as individuals. Relocation providers, serviced-apartment operators, staffing agencies and employers housing transferring staff can take the studio on a company let, invoiced monthly, with the term fixed and the end date agreed in writing. Agency introductions are welcome on your standard terms.
          </p>
          <a
            href="#enquiry"
            data-testid="company-lets-cta"
            className="btn-gold"
            onClick={(e) => {
              e.preventDefault();
              document.getElementById("enquiry")?.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
          >
            Enquire about a company let <ArrowRight size={16} />
          </a>
        </div>
      </section>

      {/* Enquiry form */}
      <section ref={formRef} className="py-20 border-t border-white/5" id="enquiry">
        <EnquiryForm />
      </section>
    </div>
  );
}


function EnquiryForm() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", arrival: "", length: "90+ nights", message: "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please tell us your name.";
    if (!form.email.trim()) e.email = "Please add your email.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "That email doesn't look right.";
    if (!form.arrival) e.arrival = "Please pick an arrival date.";
    return e;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const eMap = validate();
    setErrors(eMap);
    if (Object.keys(eMap).length) return;
    setSubmitting(true);
    try {
      const messageBody = [
        `Arrival date: ${form.arrival}`,
        `Length of stay: ${form.length}`,
        "",
        form.message || "(no additional message)",
      ].join("\n");
      await submitContact({
        full_name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || "",
        subject: `Long-stay enquiry — ${form.arrival} (${form.length})`,
        message: messageBody,
      });
      pushDataLayer("enquiry_submitted", {
        length_of_stay: form.length,
        arrival_date: form.arrival,
      });
      navigate("/long-stay/thank-you");
    } catch (err) {
      toast.error("Couldn't send your enquiry. Please try again or WhatsApp us.");
      setSubmitting(false);
    }
  };

  return (
    <div className="container mx-auto px-6 md:px-12 max-w-2xl">
      <p className="overline mb-4">Request availability</p>
      <h2 className="font-serif text-3xl sm:text-4xl text-white mb-3">Request availability</h2>
      <p className="text-white/60 mb-8">Tell me your dates and I'll come back to you the same day.</p>

      <form onSubmit={onSubmit} data-testid="enquiry-form" className="space-y-4" noValidate>
        <FormField label="Name" required error={errors.name} testid="enq-name-error">
          <input
            name="name" value={form.name} onChange={onChange}
            className={`luxe-input ${errors.name ? "border-red-500/60" : ""}`}
            data-testid="enq-name" autoComplete="name"
          />
        </FormField>

        <FormField label="Email" required error={errors.email} testid="enq-email-error">
          <input
            name="email" type="email" value={form.email} onChange={onChange}
            className={`luxe-input ${errors.email ? "border-red-500/60" : ""}`}
            data-testid="enq-email" autoComplete="email"
          />
        </FormField>

        <FormField label="Phone (optional)">
          <input
            name="phone" value={form.phone} onChange={onChange}
            className="luxe-input"
            data-testid="enq-phone" autoComplete="tel"
          />
        </FormField>

        <FormField label="Arrival date" required error={errors.arrival} testid="enq-arrival-error">
          <input
            name="arrival" type="date" value={form.arrival} onChange={onChange}
            className={`luxe-input ${errors.arrival ? "border-red-500/60" : ""}`}
            data-testid="enq-arrival"
          />
        </FormField>

        <FormField label="Length of stay">
          <select
            name="length" value={form.length} onChange={onChange}
            className="luxe-input"
            data-testid="enq-length"
          >
            <option>90+ nights</option>
            <option>30–89 nights</option>
            <option>Under 30 nights</option>
          </select>
        </FormField>

        <FormField label="Message (optional)">
          <textarea
            name="message" rows="4" value={form.message} onChange={onChange}
            className="luxe-input resize-none"
            data-testid="enq-message"
          />
        </FormField>

        <button
          type="submit"
          disabled={submitting}
          data-testid="enq-submit"
          className="btn-gold w-full justify-center"
        >
          {submitting ? "Sending…" : "Send enquiry"}
        </button>

        <p className="text-center text-white/50 text-sm pt-2">
          Or{" "}
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="enq-whatsapp"
            className="text-emerald underline underline-offset-4 hover:text-white transition-colors"
          >
            message the host on WhatsApp
          </a>
        </p>
      </form>
    </div>
  );
}

function FormField({ label, required, error, testid, children }) {
  return (
    <div>
      <label className="block text-white/50 text-[0.65rem] tracking-[0.22em] uppercase mb-2">
        {label} {required && <span className="text-gold">*</span>}
      </label>
      {children}
      {error && (
        <p data-testid={testid} className="mt-1.5 text-sm text-red-400">{error}</p>
      )}
    </div>
  );
}
